<template>
  <div v-if="loaded">
    <ManageSubsidyHeader
      :submitted-at="subsidy.submitted_at"
      :subtitle="subsidyProgram.name"
      :subtitle-subtext="subsidyProgram.description"
      :title="subsidy.child_application_data.name"
    />

    <v-container class="d-flex flex-column ga-12">
      <DashboardTasks
        @reload-tasks="loadTasks"
        :tasks="subsidyTasks"
      />

      <template v-if="subsidyProgram.enable_deferred_acceptance && offerCount > 0">
        <div class="d-flex flex-column ga-4">
          <h2>
            {{ $bct('Offers') + ' 🎉' }}
          </h2>

          <template v-if="childSchema?.meta.parent_layout?.enrollments_instructions">
            <v-card
              class="bg-primary"
              border
              tile
            >
              <v-card-text class="c-white fs-16 fw-500 markdown">
                <MarkdownContent
                  :content="childSchema?.meta.parent_layout.enrollments_instructions"
                />
              </v-card-text>
            </v-card>
          </template>

          <ManageChildSubsidyOfferList
            v-model:processing="processing"
            @changed="handleEnrollmentChange"
            ref="offerList"
            :child-data="child"
            :subsidy-program="subsidyProgram"
            data-testid="offer-list"
          />
        </div>
      </template>

      <template v-if="displayPreferencesSection">
        <div>
          <v-row>
            <v-col>
              <h2 class="mb-4">
                {{ $bct('Preferences') }}
              </h2>
            </v-col>

            <v-col
              v-if="allowAddPrograms"
              class="text-right"
            >
              <v-btn
                @click="() => $refs.childSubsidyPreferenceList.openAddProgramDialog()"
                color="primary"
                prepend-icon="add"
              >
                {{ $bct('Add provider') }}
              </v-btn>
            </v-col>
          </v-row>

          <ChildSubsidyPreferenceList
            v-model:processing="processing"
            @changed="handleEnrollmentChange"
            ref="childSubsidyPreferenceList"
            :child-data="child"
            :child-schema="childSchema"
            :show-add-button="false"
            :show-instructions="false"
            :subsidy="subsidy"
            :subsidy-program="subsidyProgram"
            data-testid="preference-list"
          />
        </div>
      </template>

      <div class="d-flex flex-column ga-4">
        <h2>
          {{ $bct('Application') }}
        </h2>

        <v-card
          v-if="childSchema?.meta.parent_layout?.applications_instructions"
          class="bg-primary"
          border
          tile
        >
          <v-card-text class="c-white fs-16 fw-500 markdown">
            <MarkdownContent :content="childSchema?.meta.parent_layout.applications_instructions" />
          </v-card-text>
        </v-card>

        <v-card
          border
          tile
        >
          <ChildSubsidyReview :value="subsidy" />
        </v-card>
      </div>

      <div
        v-if="agreements?.length > 0"
        class="d-flex flex-column ga-4"
      >
        <h2>
          {{ $bct('Agreements') }}
        </h2>

        <v-card
          border
          tile
        >
          <AgreementTable
            @change="loadAgreements"
            :agreements="agreements"
            role="parent"
          />
        </v-card>
      </div>
    </v-container>
  </div>

  <v-progress-linear
    v-else
    indeterminate
  />
</template>

<script setup>
import AgreementTable from '@/shared/components/agreement/AgreementTable.vue';
import ChildSubsidyPreferenceList from '@/parent/components/subsidy/ChildSubsidyPreferenceList.vue';
import ChildSubsidyReview from '@/parent/views/child-subsidies/ChildSubsidyReview.vue';
import DashboardTasks from '@/parent/components/dashboards/DashboardTasks.vue';
import ManageChildSubsidyOfferList from '@/parent/components/subsidy/ManageChildSubsidyOfferList.vue';
import ManageSubsidyHeader from '@/parent/components/subsidy/ManageSubsidyHeader.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import { ENROLLMENT_STATUSES } from '@/shared/assets/constants';
import useApi from '@/shared/composables/useApi';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const { api } = useApi();
const route = useRoute();
const store = useStore();
const $bct = inject('translator');

const agreements = ref([]);
const allowAddPrograms = ref(false);
const child = ref(null);
const childSubsidyPreferenceList = ref(null);
const enrollmentCount = ref(0);
const loadEnrollmentsFinished = ref(false);
const offerCount = ref(0);
const offerList = ref(null);
const processing = ref(false);
const subsidy = ref(null);
const subsidyTasks = ref([]);
const subsidyProgram = ref(null);

const childSchema = computed(() => store.state.schemas[child.value?.schema_id]);
const loaded = computed(
  () => child.value && subsidy.value && subsidyProgram.value && loadEnrollmentsFinished.value,
);

const displayPreferencesSection = computed(
  () =>
    subsidy.value?.locked &&
    (allowAddPrograms.value || subsidyProgram.value.enable_parent_enrollments_review) &&
    (enrollmentCount.value > 0 || subsidyProgram.value.enable_automatic_preference_unlock),
);

function addProgramsAllowed(enrollments) {
  if (
    subsidyProgram.value?.enable_iep_direct_placement &&
    subsidy.value?.child_application_data?.individualized_education_plan
  ) {
    return false;
  }

  if (subsidy.value?.preferences_unlocked_at) return true;
  if (!subsidyProgram.value.enable_automatic_preference_unlock) return false;

  // Handle when automatic preferences unlock is enabled
  if (enrollments.length === 0) return true;

  if (!enrollments.some((enrollment) => enrollment.prevents_automatic_preference_unlock))
    return true;

  return false;
}

async function load() {
  processing.value = true;
  const { data: subsidyData } = await api.subsidy.get(route.params.subsidyId);
  subsidy.value = subsidyData;

  const { data: subsidyProgramData } = await api.public_api.organization.subsidy_program.get(
    subsidy.value.subsidy_program_id,
  );
  subsidyProgram.value = subsidyProgramData;

  await loadEnrollments();
  await loadChild();
  await loadTasks();
  await loadAgreements();

  loaded.value = true;
  processing.value = false;
}

async function loadAgreements() {
  const response = await api.parent.agreement.index({
    member_id: store.state.profile.id,
    subsidy_id: subsidy.value.id,
  });
  agreements.value = response?.data;
}

async function loadEnrollments() {
  const resp = await api.parent.enrollment.index({ child_id: subsidy.value.child_id });

  if (resp?.data) {
    const enrollments = resp.data;
    const filteredEnrollments = enrollments.filter(
      (enrollment) => enrollment.subsidy_program_id === subsidyProgram.value.id,
    );

    allowAddPrograms.value = addProgramsAllowed(filteredEnrollments);

    const filteredOffers = filteredEnrollments.filter(
      (enrollment) =>
        enrollment.public_status === ENROLLMENT_STATUSES.OFFERED ||
        enrollment.public_status === ENROLLMENT_STATUSES.ACCEPTED,
    );

    offerCount.value = filteredOffers.length;

    enrollmentCount.value = filteredEnrollments.length;
  }
  loadEnrollmentsFinished.value = true;
}

async function loadTasks() {
  const response = await api.parent.applicationSummary.index({ subsidy_id: subsidy.value.id });
  subsidyTasks.value = response?.data;
}

async function handleEnrollmentChange() {
  await loadEnrollments();
  await loadTasks();
  childSubsidyPreferenceList.value.loadEnrollments();
  offerList.value?.loadOffers();
}

async function loadChild() {
  const resp = await api.child.get(subsidy.value.child_id);
  child.value = resp?.data;
}

onMounted(load);
</script>
